/**
 * 이상한 방 - 실내 평면도 데이터 모델(타입 · 질의)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3절(좌표) · 3.1절(셀 점유)
 *
 * ## 왜 "방 하나"가 아니라 평면도인가
 *
 * 밀폐 구획 구조는 1인칭 시절의 근거였다. 아이소메트릭 전환 뒤에도 구획·문·창은
 * 컷어웨이·조명·리빌 자막의 데이터 원천이다.
 *
 * 그래서 공간은 20m × 20m(80 × 80 셀)의 **밀폐 실내**이고, 내벽과 문으로 여러 구획이 이어진다.
 * 이 파일은 그 구획 구조를 순수 데이터로 다루며 Three.js를 알지 못한다 -
 * 렌더(`render/roomShell.ts`)·조명·오브젝트 배치 생성기·플레이어 충돌이 전부 같은 평면도를 읽어야
 * "화면에는 벽인데 걸어서 통과되는" 류의 어긋남이 원천 봉쇄된다.
 * 실제 평면도 데이터는 `floorPlans.ts`, 밴드 격자 빌더는 `bandPlan.ts`, 검증은
 * `floorPlanValidate.ts`에 있고, 호출부는 배럴 `floorPlan.ts` 하나만 본다.
 *
 * ## 핵심 규약 - 벽은 "방이 아닌 칸"이다
 *
 * `Room.rect`는 **방의 내부(벽을 제외한 실사용 영역)**를 가리킨다.
 * 벽 셀은 따로 나열하지 않고 다음 뺄셈으로 유도한다.
 *
 *   벽 셀 = 전체 바닥 셀 − 모든 방 rect − 모든 문 개구부
 *
 * 이 정의 하나가 두 가지를 공짜로 보장한다.
 *  1. **벽과 배치 가능 영역은 정의상 겹칠 수 없다.** 두 목록을 손으로 관리하면 반드시 어긋난다.
 *  2. 방 사이에 남은 틈은 자동으로 벽이 된다 - 평면도 데이터에 구멍이 나도 공간이 새지 않는다.
 *
 * 벽 두께는 1셀(0.25m)로 고정이다. 문 개구부가 정확히 그 한 칸을 관통하므로
 * "개구부가 벽을 절반만 뚫는" 경우가 존재하지 않는다.
 *
 * ## y축 취급
 *
 * 평면도는 2D(x·z)다. 높이는 `bounds.sizeY` 하나로 균일하며,
 * 3D 셀 질의(`wallCells`)에서만 y가 등장한다. 문 위쪽(상인방)은 벽으로 남아야 하므로
 * 문 개구부는 바닥부터 `DOOR_HEIGHT_CELLS`까지만 뚫린다.
 */

import { cellKey } from './grid'
import type { Cell3, RoomBounds } from './types'

/* --- 규격 상수 ------------------------------------------------------------------ */

/**
 * 벽 두께(셀). 0.25m.
 * 1로 고정한 이유는 미학이 아니라 판정이다 - 문 개구부가 벽 한 칸을 통째로 뚫으므로
 * "벽 안쪽에 낀 셀"이 생기지 않고, 벽 셀 계산이 뺄셈 한 번으로 끝난다.
 */
export const WALL_THICKNESS_CELLS = 1

/** 문 개구부 높이(셀). 8 × 0.25m = 2.0m - 실제 문 높이다. 위쪽은 상인방(벽)으로 남는다. */
export const DOOR_HEIGHT_CELLS = 8

/** 외벽 링 두께(셀). 모든 방 rect는 이 링 안쪽에만 존재해야 완전 밀폐가 보장된다. */
export const OUTER_WALL_CELLS = 1

/* --- 타입 ----------------------------------------------------------------------- */

export type RoomId = string

/**
 * 구획의 성격. 방 이름이 아니라 **시각·소품 분류**다.
 * 렌더가 바닥/벽 색을 여기서 고르고, 배치 생성기가 소품군을 여기서 고른다.
 * (그래서 '서재'처럼 태그에 없는 방은 가장 가까운 성격인 'living'을 쓴다.)
 */
export type RoomTag = 'living' | 'kitchen' | 'bedroom' | 'bath' | 'hall'

/** 축 이름. 개구부가 "어느 축을 따라 뻗는가"를 가리킬 때 쓴다. */
export type Axis = 'x' | 'z'

/** 평면도 좌표(셀). 높이는 평면도의 관심사가 아니므로 y가 없다. */
export type Cell2 = Readonly<{ x: number; z: number }>

/** 셀 단위 사각형. `x`·`z`는 **최소 코너**이고 점유는 +x/+z로 자란다(`grid.ts`와 같은 규약). */
export type CellRect = Readonly<{ x: number; z: number; sizeX: number; sizeZ: number }>

/**
 * 구획 하나. `rect`는 벽을 제외한 **내부** 영역이다.
 *
 * `name`은 **화면 문구가 아니다.** 에디터·디버그가 읽는 안정된 키 문자열이고, 사람이 읽는 이름은
 * 사전의 `room.<id>`가 준다(`i18n/ko.ts`·`en.ts`). 여기 한국어를 적으면 `core`가 화면 언어를 하나 쥐게 되고,
 * `core`는 `i18n`을 import 할 수 없으므로(레이어 방향) 그 말은 영원히 번역되지 않는다.
 * M11에서 '거실은 OO의 구획' 같은 문장이 화면에 뜨기 시작하면서 이 구분이 실제 버그가 됐다.
 */
export type Room = Readonly<{
  id: RoomId
  name: string
  tag: RoomTag
  rect: CellRect
}>

/**
 * 두 구획을 잇는 개구부(문).
 *
 * 벽이 1셀이므로 개구부는 **벽 한 칸짜리 띠**다.
 *  - `axis`가 'x'면 띠는 x축으로 `width`칸 뻗고, 벽은 `cell.z` 행에 있다.
 *  - `axis`가 'z'면 띠는 z축으로 `width`칸 뻗고, 벽은 `cell.x` 열에 있다.
 * `cell`은 띠의 최소 코너다.
 */
export type Doorway = Readonly<{
  fromRoomId: RoomId
  toRoomId: RoomId
  cell: Cell2
  width: number
  axis: Axis
}>

/**
 * 외벽에 뚫린 창.
 *
 * 창은 **바닥 셀을 비우지 않는다.** 유리는 사람도 소품도 통과할 수 없으므로
 * 배치·충돌 관점에서 창이 있는 칸은 여전히 벽이다. 창은 렌더에서만 의미를 가진다.
 */
export type WindowOpening = Readonly<{
  id: string
  roomId: RoomId
  cell: Cell2
  width: number
  axis: Axis
  /** 창턱 높이(셀). 바닥에서 이 칸부터 유리가 시작된다. */
  sillCell: number
  /** 유리 높이(셀). `sillCell + heightCells <= bounds.sizeY` 여야 한다. */
  heightCells: number
}>

/** 평면도 한 벌. 라운드가 쓰는 공간 구조 전체다. */
export type FloorPlan = Readonly<{
  id: string
  bounds: RoomBounds
  rooms: readonly Room[]
  doorways: readonly Doorway[]
  windows: readonly WindowOpening[]
}>

/* --- 키·기본 질의 --------------------------------------------------------------- */

/**
 * 평면도 셀(x·z)의 Set/Map 키.
 * `grid.ts`의 `cellKey`(3D)와 세그먼트 개수가 달라 서로 충돌하지 않는다.
 */
export function footprintKey(x: number, z: number): string {
  return `${x},${z}`
}

/** 사각형이 (x, z)를 품는가. */
export function rectContains(rect: CellRect, x: number, z: number): boolean {
  return x >= rect.x && x < rect.x + rect.sizeX && z >= rect.z && z < rect.z + rect.sizeZ
}

/** 두 사각형이 한 칸이라도 겹치는가. 방 rect 검증의 근거. */
export function rectsOverlap(a: CellRect, b: CellRect): boolean {
  return (
    a.x < b.x + b.sizeX && b.x < a.x + a.sizeX && a.z < b.z + b.sizeZ && b.z < a.z + a.sizeZ
  )
}

/** 사각형이 품는 셀 전부. 순서는 x → z 순으로 안정적이다. */
export function rectCells(rect: CellRect): readonly Cell2[] {
  const cells: Cell2[] = []
  for (let x = rect.x; x < rect.x + rect.sizeX; x += 1) {
    for (let z = rect.z; z < rect.z + rect.sizeZ; z += 1) cells.push({ x, z })
  }
  return cells
}

/** 문 개구부가 비우는 벽 셀(띠). 벽이 1셀이므로 폭 × 1칸이다. */
export function doorwayCells(doorway: Doorway): readonly Cell2[] {
  const cells: Cell2[] = []
  for (let step = 0; step < doorway.width; step += 1) {
    if (doorway.axis === 'x') cells.push({ x: doorway.cell.x + step, z: doorway.cell.z })
    else cells.push({ x: doorway.cell.x, z: doorway.cell.z + step })
  }
  return cells
}

/** 개구부(문·창)를 감싸는 사각형. 렌더가 벽면에 뚫을 구멍의 범위를 잡을 때 쓴다. */
export function openingRect(axis: Axis, cell: Cell2, width: number): CellRect {
  if (axis === 'x') return { x: cell.x, z: cell.z, sizeX: width, sizeZ: WALL_THICKNESS_CELLS }
  return { x: cell.x, z: cell.z, sizeX: WALL_THICKNESS_CELLS, sizeZ: width }
}

export function findRoom(plan: FloorPlan, roomId: RoomId): Room | undefined {
  return plan.rooms.find((room) => room.id === roomId)
}

/**
 * 방 조회. 없으면 **던진다.**
 * 존재하지 않는 방 id는 데이터 오류가 아니라 호출부의 프로그래밍 오류이며,
 * 조용히 빈 결과를 돌려주면 "벽이 안 서는 방"이 화면에만 나타나 원인 추적이 불가능해진다.
 */
export function requireRoom(plan: FloorPlan, roomId: RoomId): Room {
  const room = findRoom(plan, roomId)
  if (room === undefined) {
    throw new RangeError(`평면도 '${plan.id}'에 '${roomId}' 구획이 없다`)
  }
  return room
}

/** (x, z)를 품는 구획. 벽·문 위라면 `undefined`. */
export function roomAt(plan: FloorPlan, x: number, z: number): Room | undefined {
  return plan.rooms.find((room) => rectContains(room.rect, x, z))
}

/* --- 셀 집합 -------------------------------------------------------------------- */

/** 방 하나의 **배치 가능 바닥 셀**. rect가 곧 내부 영역이므로 벽·문이 섞일 수 없다. */
export function roomFootprintCells(plan: FloorPlan, roomId: RoomId): readonly Cell2[] {
  return rectCells(requireRoom(plan, roomId).rect)
}

/**
 * 소품을 놓아도 되는 바닥 셀 전부(모든 구획의 내부).
 * **문 개구부는 제외**한다 - 문을 막는 소품은 통행(모든 구획 도달)을 끊으므로 배치 대상이 아니다.
 */
export function placeableFootprintCells(plan: FloorPlan): ReadonlySet<string> {
  const keys = new Set<string>()
  for (const room of plan.rooms) {
    for (const cell of rectCells(room.rect)) keys.add(footprintKey(cell.x, cell.z))
  }
  return keys
}

/**
 * 플레이어가 지나갈 수 있는 바닥 셀(구획 내부 + 문 개구부).
 * 배치 가능 영역과 굳이 나눈 이유: 문 위는 **걸을 수는 있지만 놓을 수는 없는** 유일한 칸이다.
 */
export function walkableFootprintCells(plan: FloorPlan): ReadonlySet<string> {
  const keys = new Set(placeableFootprintCells(plan))
  for (const doorway of plan.doorways) {
    for (const cell of doorwayCells(doorway)) keys.add(footprintKey(cell.x, cell.z))
  }
  return keys
}

/**
 * 벽이 깔고 앉은 바닥 셀. 정의는 파일 상단 규약 그대로 **전체 − 방 − 문**이다.
 * 플레이어 충돌(수평 이동 차단)이 쓰는 집합이다.
 */
export function wallFootprintCells(plan: FloorPlan): ReadonlySet<string> {
  const walkable = walkableFootprintCells(plan)
  const keys = new Set<string>()

  for (let x = 0; x < plan.bounds.sizeX; x += 1) {
    for (let z = 0; z < plan.bounds.sizeZ; z += 1) {
      const key = footprintKey(x, z)
      if (!walkable.has(key)) keys.add(key)
    }
  }

  return keys
}

/**
 * 벽이 점유하는 **3D 셀** 전부. 키 형식은 `grid.ts`의 `cellKey`와 동일하다.
 *
 * 오브젝트 배치 생성기가 쓰는 집합이며, 문 개구부 열은 바닥부터 `DOOR_HEIGHT_CELLS`까지만
 * 비어 있고 그 위(상인방)는 벽으로 남는다 - 공중부양이 허용되므로(원칙 2) 이 구분이 없으면
 * 소품이 문 위 허공에 박혀 벽 속에 묻힌다.
 */
export function wallCells(plan: FloorPlan): ReadonlySet<string> {
  const keys = new Set<string>()
  const solidColumns = wallFootprintCells(plan)

  for (const key of solidColumns) {
    const cell = parseFootprintKey(key)
    for (let y = 0; y < plan.bounds.sizeY; y += 1) keys.add(cellKey(toCell3(cell, y)))
  }

  for (const doorway of plan.doorways) {
    for (const cell of doorwayCells(doorway)) {
      for (let y = DOOR_HEIGHT_CELLS; y < plan.bounds.sizeY; y += 1) keys.add(cellKey(toCell3(cell, y)))
    }
  }

  return keys
}

function toCell3(cell: Cell2, y: number): Cell3 {
  return { x: cell.x, y, z: cell.z }
}

/** `footprintKey`의 역함수. 내부 전용이라 형식이 깨지면 즉시 던진다. */
function parseFootprintKey(key: string): Cell2 {
  const parts = key.split(',')
  const rawX = parts[0]
  const rawZ = parts[1]
  if (parts.length !== 2 || rawX === undefined || rawZ === undefined) {
    throw new RangeError(`평면도 셀 키 형식이 아니다: '${key}'`)
  }
  return { x: Number(rawX), z: Number(rawZ) }
}

/* --- 연결성 --------------------------------------------------------------------- */

/** 문으로 이어진 인접 구획 목록(무방향). */
export function neighborRoomIds(plan: FloorPlan, roomId: RoomId): readonly RoomId[] {
  const neighbors: RoomId[] = []
  for (const doorway of plan.doorways) {
    if (doorway.fromRoomId === roomId) neighbors.push(doorway.toRoomId)
    else if (doorway.toRoomId === roomId) neighbors.push(doorway.fromRoomId)
  }
  return neighbors
}

/**
 * 시작 구획에서 문만 통해 도달 가능한 구획 전부(너비 우선 탐색).
 * 고립된 방은 "들어갈 수 없는 곳에 정답이 있는" 상태 = 원칙 2 위반이므로 테스트가 이걸로 막는다.
 */
export function reachableRoomIds(plan: FloorPlan, startRoomId: RoomId): ReadonlySet<RoomId> {
  const visited = new Set<RoomId>([startRoomId])
  const queue: RoomId[] = [startRoomId]

  while (queue.length > 0) {
    const current = queue.shift()
    if (current === undefined) break

    for (const neighbor of neighborRoomIds(plan, current)) {
      if (visited.has(neighbor)) continue
      visited.add(neighbor)
      queue.push(neighbor)
    }
  }

  return visited
}

/* --- 개구부 면 판정 --------------------------------------------------------------- */

/**
 * 개구부가 어느 방의 어느 면에 붙는가.
 * 벽이 1셀이므로 "벽 띠 바로 옆 칸이 그 방인가"만 보면 답이 나온다.
 * 렌더가 벽면 구멍을 뚫을 때도 같은 함수를 쓴다 - 판정이 두 벌이면 화면과 데이터가 갈린다.
 */
export type RoomSide = 'south' | 'north' | 'west' | 'east'

export function openingSide(room: Room, axis: Axis, cell: Cell2): RoomSide | null {
  const rect = room.rect

  if (axis === 'x') {
    if (rect.z + rect.sizeZ === cell.z) return 'north'
    if (rect.z === cell.z + WALL_THICKNESS_CELLS) return 'south'
    return null
  }

  if (rect.x + rect.sizeX === cell.x) return 'east'
  if (rect.x === cell.x + WALL_THICKNESS_CELLS) return 'west'
  return null
}

/* --- 단일 구획 어댑터 ----------------------------------------------------------- */

/**
 * 구획 하나짜리 평면도. 임의 `RoomBounds`를 평면도로 감싸는 어댑터다.
 *
 * 존재 이유: 평면도를 모르는 호출부(테스트 픽스처, 크기만 다른 임시 방)가
 * 밀폐 셸을 그대로 얻을 수 있어야 한다. 문이 없으므로 연결성 검증은 자명하게 통과한다.
 */
export function createSingleRoomPlan(bounds: RoomBounds, id = 'single-room'): FloorPlan {
  const usableX = bounds.sizeX - OUTER_WALL_CELLS * 2
  const usableZ = bounds.sizeZ - OUTER_WALL_CELLS * 2

  if (usableX < 1 || usableZ < 1) {
    throw new RangeError(
      `외벽 링을 두고 나면 남는 내부가 없다: (${bounds.sizeX}, ${bounds.sizeZ}). ` +
        `각 축이 ${OUTER_WALL_CELLS * 2 + 1}셀 이상이어야 한다`,
    )
  }

  return {
    id,
    bounds,
    rooms: [
      {
        id: 'room',
        name: 'room',
        tag: 'living',
        rect: { x: OUTER_WALL_CELLS, z: OUTER_WALL_CELLS, sizeX: usableX, sizeZ: usableZ },
      },
    ],
    doorways: [],
    windows: [],
  }
}
